import { useCallback, useEffect, useRef, useState } from "react";

import { SelectData } from "../../../../models";
import { searchCollaborators } from "../../../../service/query";
import { Status } from "../../../../service/Shared";

export interface CompanyLink {
  uuid: string;
  name: string;
}

interface UseTransactionCompanyPickerProps {
  show: boolean;
  // The company types this field offers — see CollaboratorTypeConstants.
  collaboratorTypes: string[];
  onChange: (company: CompanyLink | null) => void;
}

interface UseTransactionCompanyPickerReturnData {
  companySelectData: SelectData;
  isLoading: boolean;
  showAddCompanyModal: boolean;
  pendingCompanyName: string;
  handleTypeSearch: (term: string) => void;
  handleSelect: (collaboratorUuid: string) => void;
  handleAddNewCompany: (currentSearchTerm: string) => void;
  handleCloseAddCompanyModal: () => void;
  handleCompanyCreated: (created?: CompanyLink) => void;
}

/*
  Search-as-you-type over the caller's CRM companies, plus the inline "Add new company" flow (BR-02829/02830).
  Autocomplete already debounces onTypeSearch, so the search runs straight off each callback.
*/
export const useTransactionCompanyPicker = ({
  show,
  collaboratorTypes,
  onChange,
}: UseTransactionCompanyPickerProps): UseTransactionCompanyPickerReturnData => {
  const [companySelectData, setCompanySelectData] = useState<SelectData>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  // Carried into the add-company form so the term the user typed into the picker is not lost.
  const [pendingCompanyName, setPendingCompanyName] = useState("");

  // Monotonic id of the latest search, so a slower earlier response cannot overwrite a newer one's results.
  const requestIdRef = useRef(0);

  const fetchCompanies = useCallback(
    async (searchTerm: string) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setIsLoading(true);

      const response = await searchCollaborators({
        paging: { afterCursor: null, beforeCursor: null, limit: 25 },
        sort: [{ key: "results.name", direction: "asc" }],
        filter: {
          results: {
            collaboratorType: { operator: "in", value: collaboratorTypes },
            ...(searchTerm ? { name: { operator: "contains" as const, value: searchTerm } } : {}),
          },
        },
      });

      if (requestId !== requestIdRef.current) return;

      setIsLoading(false);
      setCompanySelectData(
        response.status === Status.Success && response.data?.results
          ? response.data.results.map((company) => ({ key: company.uuid, value: company.name }))
          : []
      );
    },
    [collaboratorTypes]
  );

  // Primed when the panel opens so the dropdown offers something before the user types.
  useEffect(() => {
    if (!show) return;
    fetchCompanies("");
  }, [fetchCompanies, show]);

  const handleTypeSearch = useCallback(
    (term: string) => {
      fetchCompanies(term);
    },
    [fetchCompanies]
  );

  /*
    Autocomplete reports an empty key while the user is typing, which is a cleared selection rather than a
    choice — the field has no company until one is picked, and a required counterparty must fail on that.
  */
  const handleSelect = useCallback(
    (collaboratorUuid: string) => {
      if (!collaboratorUuid) {
        onChange(null);
        return;
      }

      const company = companySelectData.find((option) => option.key === collaboratorUuid);
      onChange({ uuid: collaboratorUuid, name: String(company?.value ?? "") });
    },
    [companySelectData, onChange]
  );

  const handleAddNewCompany = useCallback((currentSearchTerm: string) => {
    setPendingCompanyName(currentSearchTerm);
    setShowAddCompanyModal(true);
  }, []);

  const handleCloseAddCompanyModal = useCallback(() => {
    setShowAddCompanyModal(false);
    setPendingCompanyName("");
  }, []);

  /*
    Selects the company that was just created. It is put into the options list too: the picker resolves its
    display value from that list, and a company created after the last search is not in it.
  */
  const handleCompanyCreated = useCallback(
    (created?: CompanyLink) => {
      setShowAddCompanyModal(false);
      setPendingCompanyName("");
      if (!created) return;

      setCompanySelectData((prev) =>
        prev.some((option) => option.key === created.uuid)
          ? prev
          : [{ key: created.uuid, value: created.name }, ...prev]
      );
      onChange(created);
    },
    [onChange]
  );

  return {
    companySelectData,
    isLoading,
    showAddCompanyModal,
    pendingCompanyName,
    handleTypeSearch,
    handleSelect,
    handleAddNewCompany,
    handleCloseAddCompanyModal,
    handleCompanyCreated,
  };
};
